import { defineStore } from 'pinia'
import { shallowRef, computed, markRaw } from 'vue'

import { hotReloadStore } from '@/utils/build'

import type {
  AgencyData,
  AgencyMembership,
  ProgramAccess,
  Program,
} from '@policyfly/protobuf/patrick'
import type { Role } from '@policyfly/protobuf/pfbingo'
import type { TSFixMe } from '@policyfly/types/common'
import type { ComputedRef } from 'vue'

export interface SnackbarProps {
  /**
   * Whether the snackbar is currently showing.
   */
  model: boolean
  type: string
  msg: string
  timeout: number
  close: boolean
}

export interface SnackbarOptions {
  /**
   * The message to display.
   */
  msg: string
  /**
   * The snackbar timeout.
   *
   * @default 7000
   */
  timeout?: number
  /**
   * Whether the snackbar should be closable.
   *
   * @default true
   */
  close?: boolean
}
/**
 * Shows a snackbar.
 * Pass a string to just show a message with the default options, or pass {@link SnackbarOptions} for full control.
 */
export type SnackbarFunction = (msgOrOptions: string | SnackbarOptions) => void

/**
 * A helper for each snackbar type, `default` has no color.
 */
export interface Snackbar {
  default: SnackbarFunction
  error: SnackbarFunction
  info: SnackbarFunction
  success: SnackbarFunction
  warning: SnackbarFunction
}

export type ConfirmOptions =
  | ConfirmOptionsDefault
  | ConfirmOptionsAddAgency
  | ConfirmOptionsAddAgent
  | ConfirmOptionsManageAuthenticator
  | ConfirmOptionsManageSMS
  | ConfirmOptionsManageProgramsAgency
  | ConfirmOptionsManageProgramsUser
  | ConfirmOptionsDateOption
  | ConfirmOptionsEndorsementCorrection
  | ConfirmOptionsRadioOption
  | ConfirmOptionsTextField

export type ConfirmComponentNames = Extract<ConfirmOptions, { component: string }>['component'] | 'Default'

export interface ConfirmOptionsBase {
  title?: string
  showClose?: boolean
  onlyCancel?: boolean
  /**
   * If `true` will prevent confirming if the dialog component has not yet emitted a `valid` event.
   * Any `rules` will also prevent confirming in addition to this check.
   */
  preventInvalidResolve?: boolean
  closeButton?: string
  confirmButton?: string
  cancelButton?: string
  body?: string
  onlyConfirm?: boolean
  persistent?: boolean
  maxWidth?: string | number
  noActions?: boolean
  icon?: string
  context?: unknown
}

export type ConfirmOptionsDefault = ConfirmOptionsBase

export interface ConfirmOptionsAddAgency extends ConfirmOptionsBase {
  component: 'AddAgency'
  /**
   * Values to use when initializing the form.
   */
  defaults?: {
    name: string
    agency_admin_first_name: string
    agency_admin_last_name: string
    agency_admin_email: string
    programs: number[]
    error?: string
  }
}

export interface ConfirmOptionsAddAgent extends ConfirmOptionsBase {
  component: 'AddAgent'
  context: {
    forceAdmin?: boolean
    /**
     * Programs that are available to add to
     */
    programs: Program[]
  }
  /**
   * Values to use when initializing the form.
   */
  defaults?: {
    email: string
    first_name: string
    last_name: string
    role: Role
    is_administrator: boolean
    programs: ProgramAccess[]
    error?: string
  }
}

export type ConfirmOptionsManagePrograms =
  | ConfirmOptionsManageProgramsAgency
  | ConfirmOptionsManageProgramsUser

export interface ConfirmOptionsManageProgramsAgency extends ConfirmOptionsBase {
  component: 'ManageProgramAccess'
  context: {
    /**
     * Programs that are available to select.
     */
    programs: Program[]
  }
  /**
   * Values to use when initializing the form.
   */
  defaults: {
    agency: AgencyData
    programs?: ProgramAccess[]
  }
}

export interface ConfirmOptionsManageAuthenticator extends ConfirmOptionsBase {
  component: 'ManageAuthenticator'
  defaults: {
    qrValue: string
  }
}

export interface ConfirmOptionsManageSMS extends ConfirmOptionsBase {
  component: 'ManageSms'
}

export interface ConfirmOptionsManageProgramsUser extends ConfirmOptionsBase {
  component: 'ManageProgramAccess'
  context: {
    /**
     * Programs that are available to select.
     */
    programs: Program[]
  }
  /**
   * Values to use when initializing the form.
   */
  defaults: {
    user: AgencyMembership
    programs?: ProgramAccess[]
  }
}
export interface ConfirmOptionsDateOption extends ConfirmOptionsBase {
  component: 'DateOption'
  rules?: ((options: TSFixMe) => string | true)[]
  context: string
}

export interface ConfirmOptionsEndorsementCorrection extends ConfirmOptionsBase {
  component: 'EndorsementCorrection'
  rules?: ((options: TSFixMe) => string | true)[]
}

export interface ConfirmOptionsRadioOption extends ConfirmOptionsBase {
  component: 'RadioOption'
  options: { label: string, value: string, disabled?: boolean }[]
  initialValue?: string
  label?: string
}

export interface ConfirmOptionsTextField extends ConfirmOptionsBase {
  component: 'TextField'
  label: string
  initialValue?: string
  rules?: ((options: TSFixMe) => string | true)[]
}

type ConfirmationResolve = boolean | { extra: unknown }

export type Confirmation = ConfirmOptions & {
  resolve: (value: ConfirmationResolve) => void
  value: boolean
  key: number
  persistent: boolean
  maxWidth: string | number
}

/**
 * Opens a confirmation dialog.
 * Resolves a boolean for a default dialog.
 * Other dialogs return `{ extra }` with the dialog component's data and `false` if cancelled.
 */
export interface ConfirmFunction {
  (payload: ConfirmOptionsAddAgency): Promise<{ extra: NonNullable<ConfirmOptionsAddAgency['defaults']> }>
  (payload: ConfirmOptionsAddAgent): Promise<{ extra: NonNullable<ConfirmOptionsAddAgent['defaults']> }>
  (payload: ConfirmOptionsManageProgramsAgency): Promise<{ extra: NonNullable<ConfirmOptionsManageProgramsAgency['defaults']> }>
  (payload: ConfirmOptionsManageProgramsUser): Promise<{ extra: NonNullable<ConfirmOptionsManageProgramsUser['defaults']> }>
  (payload: ConfirmOptionsManageSMS): Promise<{ extra: { number: string, token: string } } | false>
  (payload: ConfirmOptionsManageAuthenticator): Promise<{ extra: string } | false>
  (payload: ConfirmOptionsDateOption): Promise<{ extra: string } | false>
  (payload: ConfirmOptionsEndorsementCorrection): Promise<{ extra: { start: string, end: string } } | false>
  (payload: ConfirmOptionsRadioOption): Promise<{ extra: string } | false>
  (payload: ConfirmOptionsTextField): Promise<{ extra: string } | false>
  (payload: ConfirmOptionsDefault): Promise<boolean>
}

export interface NotificationStore {
  /**
   * Props to pass to the v-snackbar component.
   * To show a new snackbar use `snackbar`.
   */
  snackbarProps: ComputedRef<SnackbarProps>
  /**
   * Provides shortcuts for displaying the different snackbar types.
   */
  snackbar: Readonly<Snackbar>
  /**
   * Closes the snackbar.
   */
  closeSnackbar: () => void
  /**
   * The current confirmation dialog settings.
   * To show a new dialog use `confirm`.
   */
  confirmation: ComputedRef<Confirmation>
  /**
   * Creates a new confirmation dialog.
   */
  confirm: ConfirmFunction
  /**
   * Sets the `value` of the current confirmation dialog.
   */
  setConfirmationValue: (value: boolean) => void
  /**
   * Clears the `context` of the current confirmation dialog.
   */
  clearConfirmationContext: () => void
}

export const useNotificationStore = defineStore('notification', (): NotificationStore => {
  // snackbar
  const snackbarProps = shallowRef<SnackbarProps>({
    model: false,
    type: '',
    msg: '',
    timeout: -1,
    close: false,
  })

  function message (type: string, msgOrOptions: string | SnackbarOptions): void {
    let timeout = 7000
    let close = true
    let msg = ''
    if (typeof msgOrOptions === 'string') {
      msg = msgOrOptions
    } else {
      msg = msgOrOptions.msg
      timeout = msgOrOptions.timeout ?? timeout
      close = msgOrOptions.close ?? close
    }
    snackbarProps.value = {
      model: true,
      type,
      msg,
      timeout,
      close,
    }
  }
  const snackbar: Snackbar = markRaw({
    default: (options) => message('', options),
    error: (options) => message('error', options),
    info: (options) => message('info', options),
    success: (options) => message('success', options),
    warning: (options) => message('warning', options),
  })

  const closeSnackbar: NotificationStore['closeSnackbar'] = () => {
    snackbarProps.value = { ...snackbarProps.value, model: false }
  }

  // confirmation
  const confirmation = shallowRef<Confirmation>({
    title: '',
    persistent: false,
    key: 0,
    maxWidth: 400,
    value: false,
    resolve: () => {},
  })

  const confirm: NotificationStore['confirm'] = ((payload: ConfirmOptions): Promise<unknown> => {
    return new Promise<ConfirmationResolve>((resolve) => {
      const {
        title,
        body,
        preventInvalidResolve,
        confirmButton,
        cancelButton,
        closeButton,
        showClose,
        onlyCancel,
        onlyConfirm,
        persistent,
        maxWidth = 400,
        icon,
        noActions,
      } = payload

      confirmation.value = {
        onlyCancel,
        title,
        preventInvalidResolve,
        confirmButton,
        cancelButton,
        closeButton,
        showClose,
        body,
        resolve,
        onlyConfirm,
        persistent: persistent !== false,
        maxWidth,
        icon,
        noActions,
        component: 'component' in payload ? payload.component : undefined,
        options: 'options' in payload ? payload.options : undefined,
        context: 'context' in payload ? payload.context : undefined,
        rules: 'rules' in payload ? payload.rules : undefined,
        label: 'label' in payload ? payload.label : undefined,
        initialValue: 'initialValue' in payload ? payload.initialValue : undefined,
        defaults: 'defaults' in payload ? payload.defaults : undefined,
        value: true,
        key: Date.now(),
      }
    })
  }) as ConfirmFunction

  const setConfirmationValue: NotificationStore['setConfirmationValue'] = (value) => {
    confirmation.value = { ...confirmation.value, value }
  }

  const clearConfirmationContext: NotificationStore['clearConfirmationContext'] = () => {
    if (!('context' in confirmation.value)) return
    const cleared = { ...confirmation.value }
    delete cleared.context
    confirmation.value = cleared
  }

  return {
    // snackbar
    snackbarProps: computed(() => snackbarProps.value),
    snackbar,
    closeSnackbar,
    // confirmation
    confirmation: computed(() => confirmation.value),
    confirm,
    setConfirmationValue,
    clearConfirmationContext,
  }
})

hotReloadStore(useNotificationStore)
